import { Component, signal, output, input, inject, OnInit, viewChild, DestroyRef, computed } from '@angular/core';
import { optionalName } from '../../../../../../validators/name-rules';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize } from 'rxjs/operators';
import { ProductsFileMgt } from '../../../../../../services/product/file-management/products.file-mgt.service';
import { ProductService } from '../../../../../../services/product/product.service';
import { ProductImageItem } from '../../product-info-step/product-info-step.model';
import { AttributeSelection } from './attribute-selection/attribute-selection';
import { SubProductInfo } from './sub-product-info/sub-product-info';
import { ProductImages } from '../../product-info-step/product-images/product-images';
import { ProductPrices } from '../../product-info-step/product-prices/product-prices';
import {
  Attribute,
  CategoryLookup,
  SubProductPayload,
  SubProductTableItem,
} from '../../../../../../pages/admin-dashboard/products/add-sub-product/sub-products.model';
import {
  arabicCharactersValidator,
  englishCharactersValidator,
} from '../../../../../admin-dashboard-component/products/edit-product-form/utils/validation';
import { Error } from '../../../../../public-user-component/auth/shared/error/error';

@Component({
  selector: 'app-sub-product-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AttributeSelection, SubProductInfo, ProductImages, ProductPrices, Error],
  templateUrl: './sub-product-form.html',
    styleUrl: './sub-product-form.scss',
})
export class SubProductForm implements OnInit {
  private fb = inject(FormBuilder);
  private fileMgt = inject(ProductsFileMgt);
  private productService = inject(ProductService);
  private destroyRef = inject(DestroyRef);

  productId = input.required<number>();
  attributes = input<Attribute[]>([]);
  categories = input<CategoryLookup[]>([]);
  parentTitleEn = input<string>('');
  parentTitleAr = input<string>('');
  existingSubProducts = input<SubProductTableItem[]>([]);

  onAdded = output<SubProductTableItem>();
  attrSelection = viewChild.required(AttributeSelection);

  loading = signal(false);
  errorMessage = signal<string | null>(null);
  selectedValues = signal<Record<string, string> | null>(null);
  selectedImages = signal<ProductImageItem[]>([]);
  imageErrors = signal<string[]>([]);
  duplicateRowIndex = signal<number | null>(null);

  priceWarnings = computed(() => {
    const vals = this.subProductForm.getRawValue();
    const people = vals.peoplePrice ? Number(vals.peoplePrice) : null;
    const sellerP = parseFloat(vals.sellerPrice || '0');
    const pharmacyP = parseFloat(vals.pharmacyPrice || '0');
    const warnings: string[] = [];
    if (people && pharmacyP > people)
      warnings.push($localize`:@@subProduct.validation.pharmacyGtPeople:Pharmacy Price cannot exceed People Price`);
    if (sellerP > 0 && pharmacyP > 0 && pharmacyP < sellerP)
      warnings.push(
        $localize`:@@subProduct.validation.pharmacyLtSeller:Pharmacy Price cannot be less than Seller Price`,
      );
    return warnings;
  });

  subProductForm = this.fb.group({
    titleEn: [''],
    titleAr: ['', [Validators.required, Validators.maxLength(255)]],
    descriptionEn: ['', [Validators.maxLength(2000), englishCharactersValidator()]],
    descriptionAr: ['', [Validators.maxLength(2000), arabicCharactersValidator()]],
    category: [''],
    subCategory: [''],
    barcode: ['', [Validators.minLength(13), Validators.maxLength(13), Validators.pattern(/^\d{13}$/)]],
    peoplePrice: [null as number | null, [Validators.min(0.01), Validators.max(1000000)]],
    sellerDiscount: [null as number | null, [Validators.min(0), Validators.max(100)]],
    sellerPrice: [{ value: '', disabled: true }],
    pharmacyDiscount: [null as number | null, [Validators.min(0), Validators.max(100)]],
    pharmacyPrice: [{ value: '', disabled: true }],
    isRefrigerated: [false],
    zoneId: [null as number | null],
  });

  get existingCombinations(): Record<string, string>[] {
    return this.existingSubProducts().map((sp) => sp.attributeValues ?? {});
  }

  ngOnInit(): void {
    this.subProductForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.calcPrices();
      this.checkDuplicate();
    });
  }

  private calcPrices(): void {
    const people = this.subProductForm.get('peoplePrice')?.value;
    const sD = this.subProductForm.get('sellerDiscount')?.value;
    const pD = this.subProductForm.get('pharmacyDiscount')?.value;

    let sR = '';
    if (people && people > 0 && sD !== null && sD !== undefined) {
      const discountedPrice = people - (people * Number(sD)) / 100;
      sR = discountedPrice.toFixed(2);
    }

    let pR = '';
    if (people && people > 0 && pD !== null && pD !== undefined) {
      const discountedPrice = people - (people * Number(pD)) / 100;
      pR = discountedPrice.toFixed(2);
    }

    this.subProductForm.get('sellerPrice')?.setValue(sR, { emitEvent: false });
    this.subProductForm.get('pharmacyPrice')?.setValue(pR, { emitEvent: false });
  }

  private checkDuplicate(): void {
    const current = this.selectedValues();
    if (!current || Object.keys(current).length === 0) {
      this.duplicateRowIndex.set(null);
      return;
    }
    const idx = this.existingSubProducts().findIndex(
      (sp) => JSON.stringify(sp.attributeValues) === JSON.stringify(current),
    );
    this.duplicateRowIndex.set(idx !== -1 ? idx : null);
  }

  handleFileSelect(files: File[]): void {
    const remaining = 5 - this.selectedImages().length;
    const valid = files.slice(0, remaining).filter((file) => file.size <= 5 * 1024 * 1024);
    valid.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e: any) =>
        this.selectedImages.update((prev) => [
          ...prev,
          { file, preview: e.target.result, isPrimary: prev.length === 0 },
        ]);
      reader.readAsDataURL(file);
    });
  }

  setPrimary(index: number): void {
    this.selectedImages.update((imgs) => imgs.map((img, i) => ({ ...img, isPrimary: i === index })));
  }

  removeImg(index: number): void {
    this.selectedImages.update((imgs) => {
      const updated = imgs.filter((_, i) => i !== index);
      if (imgs[index].isPrimary && updated.length > 0) updated[0].isPrimary = true;
      return updated;
    });
  }

  onAttributeSelectionChange(vals: Record<string, string> | null): void {
    this.selectedValues.set(vals);
    this.checkDuplicate();
  }

  addSubProduct(): void {
    this.errorMessage.set(null);
    const currentVals = this.selectedValues();
    const hasAtLeastOneAttr = currentVals && Object.values(currentVals).some((v) => !!v);

    if (!hasAtLeastOneAttr || this.subProductForm.invalid) {
      this.subProductForm.markAllAsTouched();
      this.attrSelection().markAllTouched();
      if (!hasAtLeastOneAttr)
        this.errorMessage.set($localize`:@@subProduct.error.atLeastOne:Please select at least one attribute.`);
      return;
    }

    if (this.duplicateRowIndex() !== null) {
      this.errorMessage.set($localize`:@@subProduct.error.duplicate:This combination already exists.`);
      return;
    }

    if (this.selectedImages().length === 0) {
      this.imageErrors.set([$localize`:@@subProduct.validation.imageRequired:Required`]);
      return;
    }

    this.loading.set(true);
    const uploads = this.selectedImages().map((img) =>
      this.fileMgt.uploadImage(img.file).pipe(catchError(() => of({ fid: '' }))),
    );

    forkJoin(uploads)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (results: any[]) => {
          const fids = results.map((r) => r.fid).filter((f) => !!f);
          const vals = this.subProductForm.getRawValue();
          const primaryIdx = this.selectedImages().findIndex((i) => i.isPrimary);

          const payload: SubProductPayload = {
            titleEn: optionalName(vals.titleEn),
            titleAr: vals.titleAr!.trim(),
            descriptionEn: vals.descriptionEn || undefined,
            descriptionAr: vals.descriptionAr || undefined,
            categoryId: vals.category ? Number(vals.category) : undefined,
            subCategoryId: vals.subCategory ? Number(vals.subCategory) : undefined,
            barcode: vals.barcode || undefined,
            customerPrice: vals.peoplePrice ? Number(vals.peoplePrice) : undefined,
            sellerPriceDiscountPercentage: Number(vals.sellerDiscount) || 0,
            businessPriceDiscountPercentage: Number(vals.pharmacyDiscount) || 0,
            profileImageFid: fids[primaryIdx >= 0 ? primaryIdx : 0] || fids[0] || '',
            imagesFids: fids,
            productAttributeValueIds: Object.values(this.selectedValues()!)
              .filter((v) => !!v)
              .map(Number),
            isRefrigerated: !!vals.isRefrigerated,
            zoneId: vals.zoneId ? Number(vals.zoneId) : undefined,
          };

          this.productService.createSingleSubProduct(this.productId(), payload).subscribe({
            next: (res: any) => {
              this.onAdded.emit({
                id: res.id,
                titleEn: payload.titleEn ?? '',
                titleAr: payload.titleAr,
                barcode: payload.barcode || '',
                customerPrice: payload.customerPrice || 0,
                sellerPrice: vals.sellerPrice || '',
                pharmacyPrice: vals.pharmacyPrice || '',
                preview: this.selectedImages().find((i) => i.isPrimary)?.preview,
                attributeValues: { ...this.selectedValues()! },
              });
              this.resetForm();
            },
            error: (err) => this.errorMessage.set(err.messageEn || 'Creation failed'),
          });
        },
      });
  }

  private resetForm(): void {
    this.subProductForm.reset();
    this.attrSelection().reset();
    this.selectedImages.set([]);
    this.selectedValues.set(null);
    this.duplicateRowIndex.set(null);
    this.imageErrors.set([]);
  }
}
